<template>
  <div ref="orderListRef" v-loading="isLoading" class="order-list" @scroll="scrollHandle">
    <template v-if="orderList.length">
      <template v-for="item in orderList" :key="item.id">
        <OrderItem
          :item="item"
          @pay="orderPayHandle"
          @detail="orderDetailHandle"
          @cancel="orderCancelHandle"
          @delete="orderDeleteHandle"
          @confirm="orderConfirmHandle"
          @logistics="orderLogisticsHandle"
          @evaluate="orderEvaluateHandle"
        />
      </template>
    </template>
    <template v-else>
      <div class="empty">您还没有相关的订单</div>
    </template>

    <PayTypeDialog ref="payTypeDialogRef" @confirm="payTypeConfirmHandle" />
    <PayQrCodeDialog ref="payQrCodeDialogRef" />
    <ExpressTrackDialog ref="expressTrackDialogRef" />
    <OrderEvaluateDialog ref="orderEvaluateDialogRef" @success="refreshOrderList" />
  </div>
</template>

<script setup>
  import {
    ref,
    unref,
    watch,
    onWatcherCleanup,
    defineProps,
    useTemplateRef,
    onActivated
  } from 'vue'
  import { useRouter } from 'vue-router'
  import OrderItem from './OrderItem.vue'
  import ExpressTrackDialog from './ExpressTrackDialog.vue'
  import PayTypeDialog from '@/components/PayTypeDialog.vue'
  import PayQrCodeDialog from '@/components/PayQrCodeDialog.vue'
  import OrderEvaluateDialog from '@/components/OrderEvaluateDialog.vue'
  import { getOrderList, orderCancel, orderConfirm, orderDelete } from '@/api/user'
  import { payApi } from '@/api/custom'
  import { filterEmptyObject } from '@/utils'
  import { ElMessage, ElMessageBox } from 'element-plus'

  const props = defineProps({
    statuses: {
      type: Array,
      default: () => []
    }
  })

  const router = useRouter()

  const orderListRef = useTemplateRef('orderListRef')
  const orderEvaluateDialogRef = useTemplateRef('orderEvaluateDialogRef')

  const orderList = ref([])
  const orderSearchParams = ref({
    page: 1,
    type: 'shop-goods-order'
  })
  const isLoading = ref(false)
  const hasMore = ref(true)

  let orderRequestControl
  const orderSearchHandle = async () => {
    isLoading.value = true

    const params = {
      statuses: props.statuses,
      ...unref(orderSearchParams),
      lastId:
        unref(orderSearchParams).page !== 1 && unref(orderList).length
          ? unref(orderList).at(-1).id
          : '',
      count: 5
    }

    const result =
      (await getOrderList({
        ...filterEmptyObject({
          ...params,
          signal: orderRequestControl?.signal
        })
      })) ?? []

    if (unref(orderSearchParams).page === 1) {
      orderList.value = result
    } else {
      orderList.value.push(...result)
    }

    hasMore.value = result.length >= 10
    isLoading.value = false
  }
  const refreshOrderList = () => {
    const oldPage = unref(orderSearchParams).page
    orderSearchParams.value.page = 1
    orderList.value.length = 0
    oldPage == 1 && orderSearchHandle()
  }

  let scrollTimer = null
  const scrollHandle = () => {
    scrollTimer && clearTimeout(scrollTimer)
    scrollTimer = setTimeout(() => {
      const { scrollHeight, clientHeight, scrollTop } = unref(orderListRef)
      const isLoadMore = scrollHeight - (clientHeight + scrollTop) <= 100
      if (isLoadMore && !unref(isLoading) && unref(hasMore)) {
        orderSearchParams.value.page += 1
      }
    }, 100)
  }

  watch(() => props.statuses, refreshOrderList)
  watch(
    () => unref(orderSearchParams),
    () => {
      orderRequestControl = new AbortController()
      onWatcherCleanup(() => orderRequestControl.abort())
      orderSearchHandle()
    },
    {
      immediate: true,
      deep: true
    }
  )

  let payOrderData = {}
  const payTypeDialogRef = useTemplateRef('payTypeDialogRef')
  const payQrCodeDialogRef = useTemplateRef('payQrCodeDialogRef')
  const orderPayHandle = async data => {
    payOrderData = data
    unref(payTypeDialogRef)?.show()
  }
  const payTypeConfirmHandle = async payType => {
    try {
      const payData = await payApi({
        id: payOrderData.id,
        channel: payType,
        platform: 'PCWEB'
      })
      if (payData) {
        unref(payQrCodeDialogRef)?.show({
          payType,
          price: payOrderData.amount,
          qrCodeUrl: payData,
          validityTime: 180
        })
      } else {
        ElMessage.error('支付失败')
      }
    } catch (error) {
      ElMessage.error(error.message || '支付失败')
      console.log(error, 'error')
    }
  }

  const orderDetailHandle = async id => {
    router.push({ path: '/order-detail', query: { id } })
  }

  const orderCancelHandle = id => {
    ElMessageBox.confirm('是否取消订单？')
      .then(async () => {
        const result = await handleRequest(orderCancel, id)
        if (result) {
          ElMessage.success('取消成功')
          refreshOrderList()
        }
      })
      .catch(() => {})
  }

  const orderDeleteHandle = id => {
    ElMessageBox.confirm('是否删除订单？')
      .then(async () => {
        const result = await handleRequest(orderDelete, id)
        if (result) {
          ElMessage.success('删除成功')
          refreshOrderList()
        }
      })
      .catch(() => {})
  }

  const orderConfirmHandle = id => {
    ElMessageBox.confirm('是否确认收货？')
      .then(async () => {
        const result = await handleRequest(orderConfirm, id)
        if (result) {
          ElMessage.success('确认收货成功')
          refreshOrderList()
        }
      })
      .catch(() => {})
  }

  const expressTrackDialogRef = useTemplateRef('expressTrackDialogRef')
  const orderLogisticsHandle = id => {
    unref(expressTrackDialogRef)?.show(id)
  }

  const orderEvaluateHandle = id => {
    unref(orderEvaluateDialogRef)?.show(id)
  }

  const handleRequest = async (fn, ...args) => {
    if (typeof fn !== 'function') return
    try {
      return await fn.call({}, ...args)
    } catch (error) {
      ElMessage.error(error)
    }
  }

  onActivated(refreshOrderList)
</script>

<style lang="scss" scoped>
  .order-list {
    flex: 1;
    overflow: auto;
    margin: -20px;

    .empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 100%;
      font-size: 16px;
      background-color: #fff;
    }
  }
</style>
